import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@copro/api";
import { _3_DAYS } from "../../../constants/durations";
import {
  isTimelineEnabled,
  TIMELINE_FILTERS,
  type TimelineEntityTypeFilter,
  type TimelineFilters,
} from "./useGetActivityTimeline";
import { keys } from "./keys";

type TimelineCounts = Record<TimelineEntityTypeFilter, number>;

// The list is paged, so the filter chips' counts come from the server, in one
// grouped query rather than one COUNT per type over the UNION view.
async function getActivityTimelineCounts(filters: TimelineFilters): Promise<TimelineCounts> {
  const { data, error } = await apiClient.rpc('get_activity_timeline_counts', {
    _subject_id: filters.subTopicId,
    _provider_id: filters.providerId,
  });

  if (error) {
    throw error;
  }

  const counts = Object.fromEntries(
    TIMELINE_FILTERS.map((type) => [type, 0])
  ) as TimelineCounts;

  for (const row of data ?? []) {
    counts.all += row.item_count;
    // The function reports "Autre action" rows under 'other', apart from 'communication'.
    if (row.entity_type in counts) {
      counts[row.entity_type as TimelineEntityTypeFilter] = row.item_count;
    }
  }

  return counts;
}

export function useGetActivityTimelineCounts(filters: TimelineFilters) {
  return useQuery({
    queryKey: keys.activityTimelineCounts(filters),
    queryFn: () => getActivityTimelineCounts(filters),
    enabled: isTimelineEnabled(filters),
    staleTime: _3_DAYS,
  });
}
